import React from "react";
import { useTheme } from "@emotion/react";
import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Close from "@mui/icons-material/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";

import Box from "@vapor/react-material/Box";
import Toolbar from "@vapor/react-material/Toolbar";
import AppBar from "@vapor/react-material/AppBar";
import Stack from "@vapor/react-material/Stack";
import IconButton from "@vapor/react-material/IconButton";

let warnedOnce = false;

const warn = () => {
  if (!warnedOnce) {
    console.warn(
      [
        "Vapor: The VaporAppBar component will be moved from `@vapor/react-custom` to `@vapor/react-uishell`.",
        "",
        "",
        "For more information you may want to check Vapor UIShell CHANGELOG https://biosphere.teamsystem.com/vapor/react/vapor-uishell/-/blob/develop/CHANGELOG.md"
      ].join("\n")
    );
  }

  warnedOnce = true;
};

type AppBarProps = {
  menuIcon?: React.ReactNode;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  isDrawerOpen?: boolean;
  toggleDrawer?: React.MouseEventHandler<HTMLButtonElement>;
  disableDrawer?: boolean;
};

const drawerWidth = 248;

const AppBarXl = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "disableDrawer"
})<{ open?: boolean; disableDrawer?: boolean }>(
  ({ theme, open, disableDrawer }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    ...((!disableDrawer &&
      open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen
        })
      }) ||
      {})
  })
);

export const VaporAppBar = styled(
  (props: AppBarProps) => {
    const {
      menuIcon,
      leftContent,
      rightContent,
      isDrawerOpen,
      toggleDrawer,
      disableDrawer,
      ...otherProps
    } = props;
    const theme: any = useTheme();
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
    warn();

    return (
      <>
        <AppBar
          position="absolute"
          open={disableDrawer ? disableDrawer : isDrawerOpen}
          sx={{
            width: "-webkit-fill-available",
            display: { xs: "block", lg: "none" }
          }}
        >
          <Toolbar
            {...otherProps}
            disableGutters
            variant="navigation"
            size="small"
            sx={{
              "&.MuiToolbar-root": {
                paddingRight: 0,
                paddingLeft: 0
              }
            }}
          >
            <Box>
              <Stack direction="row" alignItems="center">
                {!disableDrawer && (
                  <IconButton size="large" onClick={toggleDrawer}>
                    {isDrawerOpen ? (
                      <Close sx={{ color: "white" }} />
                    ) : (
                      menuIcon || (
                        <FontAwesomeIcon
                          style={{ padding: 4, width: 16, height: 16 }}
                          icon={faBars}
                        />
                      )
                    )}
                  </IconButton>
                )}
                {leftContent || null}
              </Stack>
            </Box>
            <Box>
              <Stack direction="row" alignItems="center">
                {rightContent || null}
              </Stack>
            </Box>
          </Toolbar>
        </AppBar>
        <AppBarXl
          position="absolute"
          open={disableDrawer ? disableDrawer : isDrawerOpen}
          disableDrawer={disableDrawer}
          sx={{
            width: "-webkit-fill-available",
            display: { xs: "none", lg: "block" }
          }}
        >
          <Toolbar
            {...otherProps}
            disableGutters
            variant="navigation"
            size="small"
            sx={{
              "&.MuiToolbar-root": {
                paddingRight: 0,
                paddingLeft: 0
              }
            }}
          >
            <Box>
              <Stack direction="row" alignItems="center">
                {!disableDrawer && !isDrawerOpen && (
                  <IconButton size="large" onClick={toggleDrawer}>
                    {menuIcon || (
                      <FontAwesomeIcon
                        style={{ padding: 4, width: 16, height: 16 }}
                        icon={faBars}
                      />
                    )}
                  </IconButton>
                )}
                {leftContent || null}
              </Stack>
            </Box>
            <Box>
              <Stack direction="row" alignItems="center">
                {rightContent || null}
              </Stack>
            </Box>
          </Toolbar>
        </AppBarXl>
      </>
    );
  },
  {
    name: "VaporAppBar",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)(({ theme }) => ({}));
